import type { BoxProps } from '@mui/material/Box';
import { varFade, MotionViewport } from '@/components/animate';
import { Carousel, useCarousel, CarouselDotButtons } from '@/components/carousel';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { m } from 'framer-motion';
import { Button, Paper, useTheme } from '@mui/material';
import { CardApplicationsItem } from '@/types/CardApplicationsItem';
import { CarouselCardsSection } from '@/types/sections/CarouselCardsSection';
import { getCtaUrl } from '@/types/Cta';
import Icon, { getIconName } from '@/components/Icon/Icon';

type CarouselCardsProps = BoxProps & {
  section: CarouselCardsSection;
};

export default function CarouselCards({ sx, section, ...other }: CarouselCardsProps) {
  const carousel = useCarousel({
    align: 'start',
    containScroll: false,
    slidesToShow: { xs: 1, sm: 2.5 },
    slideSpacing: '50px',
  });

  return (
    <Box
      component="section"
      sx={[{ overflow: 'hidden' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}>
      <Container component={MotionViewport} sx={{ textAlign: 'center', py: { xs: 2, md: 10 } }}>
        {section?.small && (
          <m.div variants={varFade('inDown')}>
            <Typography variant="overline" sx={{ color: 'text.disabled' }}>
              {section?.small}
            </Typography>
          </m.div>
        )}

        {section?.title && (
          <m.div variants={varFade('inUp')}>
            <Typography
              variant="h3"
              align="center"
              sx={{ pb: { xs: 2, md: 3 }, color: 'primary.main' }}>
              {section?.title}
            </Typography>
          </m.div>
        )}

        {section?.subtitle && (
          <m.div variants={varFade('inUp')}>
            <Typography sx={{ mx: 'auto', pb: 3, maxWidth: 640, color: 'text.secondary' }}>
              {section?.subtitle}
            </Typography>
          </m.div>
        )}

        <m.div variants={varFade('inUp')}>
          <Box sx={{ position: 'relative', alignItems: 'center' }}>
            <Carousel carousel={carousel}>
              {section?.cards?.map((item, index) => (
                <CarouselItem
                  key={index}
                  index={index}
                  item={{
                    id: `carousel-item-${index}`,
                    title: item.title,
                    subtitle: item.subtitle,
                    image: item.image,
                    cta1: item.cta1,
                  }}
                />
              ))}
            </Carousel>
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                alignItems: 'end',
                flexDirection: 'column',
                bgcolor: 'background.neutral',
              }}>
              <CarouselDotButtons
                variant="rounded"
                scrollSnaps={carousel.dots.scrollSnaps}
                selectedIndex={carousel.dots.selectedIndex}
                onClickDot={carousel.dots.onClickDot}
                sx={{ color: 'primary.main' }}
              />
            </Box>
          </Box>
        </m.div>
      </Container>
    </Box>
  );
}

// ----------------------------------------------------------------------

type CarouselItemProps = {
  index: number;
  item: { id: string } & CardApplicationsItem;
};

function CarouselItem({ item, index }: CarouselItemProps) {
  const theme = useTheme();

  return (
    <Paper
      elevation={2}
      sx={{
        borderRadius: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        textAlign: 'start',
        display: 'flex', // Flex container
        flexDirection: 'column', // Stack items vertically
        justifyContent: 'space-between', // Ensure space between content and button
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: theme.vars.customShadows.card,
        },
      }}>
      <Box padding={2} sx={{ flex: '1 0 auto' }}>
        {/* Image */}
        <Box
          component="img"
          alt={item.title}
          src={item.image?.url || ''}
          sx={{
            aspectRatio: '4/3',
            objectFit: 'cover',
            width: '100%',
            borderRadius: 1,
          }}
        />

        {/* Title */}
        <Typography variant="h3" sx={{ my: 3, color: 'primary.main' }}>
          {item.title}
        </Typography>

        {/* Subtitle */}
        <Typography variant="body1" sx={{ maxWidth: 640, color: 'text.secondary' }}>
          {item.subtitle}
        </Typography>
      </Box>

      {/* CTA Button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          mt: 'auto', // Pushes the button to the bottom of the card
          padding: 2, // Add padding for spacing
        }}>
        {item.cta1 && (item.cta1.title || item.cta1?.icon) && (
          <Button
            size="medium"
            variant="contained"
            color="primary"
            href={getCtaUrl(item.cta1)}
            sx={{
              borderRadius: 0,
            }}
            startIcon={
              item.cta1.icon && (
                <Icon
                  name={getIconName(item.cta1.icon, 'unknown')}
                  color={theme.palette.common.white}
                />
              )
            }>
            {item?.cta1.title}
          </Button>
        )}
      </Box>
    </Paper>
  );
}
